import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    const checkMobile = () => {
      setIsMobileView(window.innerWidth <= 992);
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkMobile);
    
    // Initial check
    checkMobile();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <NavbarContainer isScrolled={isScrolled}>
        <LogoSection>
          <LogoLink to="/" onClick={closeMobileMenu}>
            <Logo src="logo.png" alt="Finance Logo" />
            <LogoText>FinSecure</LogoText>
          </LogoLink>
        </LogoSection>

        <NavSection isScrolled={isScrolled}>
          <CurveClip isScrolled={isScrolled} />
          
          {!isMobileView && (
            <>
              <NavMenu>
                <NavItem>
                  <NavLink to="/loans">Loans</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/invest">Investments</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/insu">Insurance</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/cont">Contact Us</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/about">About</NavLink>
                </NavItem>
              </NavMenu>

              <ButtonGroup>
                <ApplyButton 
                  as="button" 
                  type="button"
                  data-bs-toggle="modal" 
                  data-bs-target="#loanApplicationModal"
                >
                  <ButtonText>APPLY LOAN</ButtonText>
                  <ButtonIcon>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
                    </svg>
                  </ButtonIcon>
                </ApplyButton>
              </ButtonGroup>
            </>
          )}
          
          {isMobileView && (
            <HamburgerButton onClick={toggleMobileMenu} isOpen={isMobileMenuOpen}>
              <span></span>
              <span></span>
              <span></span>
            </HamburgerButton>
          )}
        </NavSection>
      </NavbarContainer>

      {/* Mobile Menu Drawer */}
      <MobileDrawer isOpen={isMobileMenuOpen}>
        <MobileMenu>
          <MobileNavItem>
            <MobileNavLink to="/loans" onClick={closeMobileMenu}>Loans</MobileNavLink>
          </MobileNavItem>
          <MobileNavItem>
            <MobileNavLink to="/invest" onClick={closeMobileMenu}>Investments</MobileNavLink>
          </MobileNavItem>
          <MobileNavItem>
            <MobileNavLink to="/insu" onClick={closeMobileMenu}>Insurance</MobileNavLink>
          </MobileNavItem>
          <MobileNavItem>
            <MobileNavLink to="/cont" onClick={closeMobileMenu}>Contact Us</MobileNavLink>
          </MobileNavItem>
          <MobileNavItem>
            <MobileNavLink to="/about" onClick={closeMobileMenu}>About</MobileNavLink>
          </MobileNavItem>
          
          <MobileButtonGroup>
            <MobileApplyButton 
              as="button" 
              type="button"
              data-bs-toggle="modal" 
              data-bs-target="#loanApplicationModal"
              onClick={closeMobileMenu}
            >
              <ButtonText>APPLY LOAN</ButtonText>
              <ButtonIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
                </svg>
              </ButtonIcon>
            </MobileApplyButton>
          </MobileButtonGroup>
        </MobileMenu>
        
        <CloseButton onClick={closeMobileMenu}>
          <CloseIcon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
              <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
            </svg>
          </CloseIcon>
        </CloseButton>
      </MobileDrawer>
      
      {/* Overlay for when mobile menu is open */}
      <Overlay isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
    </>
  );
};

// Animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

// Styled Components
const NavbarContainer = styled.nav`
  display: flex;
  height: 70px;
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: ${props => props.isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none'};
  
  @media (max-width: 992px) {
    height: 60px;
  }
`;

const LogoSection = styled.div`
  width: 260px;
  background: white;
  display: flex;
  align-items: center;
  padding: 0 24px;
  z-index: 3;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: -20px;
    top: 0;
    height: 100%;
    width: 20px;
    background: linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%);
    z-index: 2;
  }
  
  @media (max-width: 992px) {
    width: 200px;
    padding: 0 16px;
    
    &::after {
      right: -15px;
      width: 15px;
    }
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const Logo = styled.img`
  height: 38px;
  width: auto;
  margin-right: 12px;
  transition: all 0.3s ease;
  
  ${LogoLink}:hover & {
    transform: rotate(-5deg);
  }
  
  @media (max-width: 992px) {
    height: 32px;
    margin-right: 8px;
  }
`;

const LogoText = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: #c91429;
  letter-spacing: -0.5px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: #c91429;
    transition: width 0.3s ease;
  }
  
  ${LogoLink}:hover &::after {
    width: 100%;
  }
  
  @media (max-width: 992px) {
    font-size: 18px;
  }
`;

const NavSection = styled.div`
  flex: 1;
  background: #c91429;
  border-bottom-left-radius: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
  padding-left: 60px;
  position: relative;
  transition: all 0.3s ease;
  
  ${props => props.isScrolled && css`
    border-bottom-left-radius: 20px;
  `}
  
  @media (max-width: 1200px) {
    padding: 0 30px;
    padding-left: 50px;
  }
  
  @media (max-width: 992px) {
    border-bottom-left-radius: 20px;
    padding: 0 20px;
    padding-left: 40px;
    justify-content: flex-end;
  }
`;

const CurveClip = styled.div`
  position: absolute;
  left: -30px;
  top: 0;
  height: 100%;
  width: 30px;
  background: #c91429;
  border-top-right-radius: 15px;
  transition: all 0.3s ease;
  
  ${props => props.isScrolled && css`
    border-top-right-radius: 8px;
  `}
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 15px;
    background: white;
    border-bottom-left-radius: 15px;
  }
  
  @media (max-width: 992px) {
    left: -20px;
    width: 20px;
    border-top-right-radius: 8px;
    
    &::before {
      width: 10px;
      border-bottom-left-radius: 8px;
    }
  }
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 32px;
  
  @media (max-width: 1200px) {
    gap: 24px;
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
  padding: 10px 0;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    color: #ffd700;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: #ffd700;
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
  
  @media (max-width: 1200px) {
    font-size: 14px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const ApplyButton = styled(Link)`
  padding: 12px 28px;
  border-radius: 30px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  border: none;
  outline: none;
  cursor: pointer;
  gap: 8px;
  background: linear-gradient(145deg, #ffd700, #ffb700);
  color: #8b0000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    outline: none;
    box-shadow: none;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: all 0.6s ease;
  }
  
  &:hover {
    animation: ${pulse} 0.5s ease;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 1200px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const ButtonText = styled.span`
  font-size: 14px;
  letter-spacing: 0.5px;
  
  @media (max-width: 1200px) {
    font-size: 12px;
  }
`;

const ButtonIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Mobile Menu Styles
const HamburgerButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1001;
  
  @media (max-width: 992px) {
    display: flex;
  }
  
  span {
    width: 30px;
    height: 3px;
    background: white;
    border-radius: 10px;
    transition: all 0.3s linear;
    position: relative;
    transform-origin: 1px;
    
    &:first-child {
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }
    
    &:nth-child(2) {
      opacity: ${props => props.isOpen ? '0' : '1'};
      transform: ${props => props.isOpen ? 'translateX(20px)' : 'translateX(0)'};
    }
    
    &:nth-child(3) {
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

const MobileDrawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100vh;
  background: white;
  z-index: 1002;
  padding: 80px 30px 30px;
  box-shadow: -5px 0 25px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;
  
  @media (max-width: 480px) {
    width: 280px;
  }
`;

const MobileMenu = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MobileNavItem = styled.li`
  position: relative;
`;

const MobileNavLink = styled(Link)`
  color: #333;
  text-decoration: none;
  font-weight: 600;
  font-size: 18px;
  padding: 12px 0;
  display: block;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    color: #c91429;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: #c91429;
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const MobileButtonGroup = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: center;
`;

const MobileApplyButton = styled(ApplyButton)`
  padding: 14px 32px;
  font-size: 16px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c91429;
  font-size: 24px;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: ${props => props.isOpen ? 'block' : 'none'};
  animation: ${props => props.isOpen ? fadeIn : fadeOut} 0.3s ease-in-out;
`;

export default Navbar;